import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useRules } from '../context/RuleContext';
import { validateFeeRule } from '../utils/validator';

const INITIAL_STATE = {
  carrier: '',
  sequenceNum: '',
  origin: '',
  destination: '',
  feeType: '',
  feeAmount: '',
  feePercent: '',
  oncePerBookingFeeAmount: '',
  tripType: '',
  cabinClass: '',
  cabinClassMatchMode: '',
  passengerType: '',
  priceSource: '',
  travelDateEffective: '',
  travelDateDiscontinue: '',
  ticketingDateEffective: '',
  ticketingDateDiscontinue: '',
  minPrice: '',
  maxPrice: '',
  minSegments: '',
  minTotalSegments: '',
  interline: '',
  sliceInterline: '',
  layoverMinutes: '',
  maxHoursToDeparture: '',
};

const steps = ['Basic Info', 'Route & Filters', 'Dates & Price'];

export default function AddRule({ editingRule, isCopying, onDone }) {
  const { addFeeRule, insertFeeRule, updateFeeRule, feeRules, locationGroups, carrierGroups } = useRules();
  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [insertMode, setInsertMode] = useState(false); // When true, inserting shifts existing rules

  const isEditing = !!editingRule && !isCopying;

  useEffect(() => {
    if (editingRule && !isCopying) {
      // Editing existing rule
      let feeType = '';
      if (editingRule.suppress === 'true' || editingRule.suppress === true) feeType = 'Suppress';
      else if (editingRule.oncePerBookingFeeAmount) feeType = 'OncePerBookingFeeAmount';
      else if (editingRule.feeAmount && editingRule.feePercent) feeType = 'FeeAmountAndPercent';
      else if (editingRule.feeAmount) feeType = 'FeeAmount';
      else if (editingRule.feePercent !== undefined && editingRule.feePercent !== null && editingRule.feePercent !== '') feeType = 'FeePercent';

      setForm({
        ...INITIAL_STATE,
        ...editingRule,
        sequenceNum: String(editingRule.sequenceNum || ''),
        feePercent: editingRule.feePercent !== undefined && editingRule.feePercent !== null ? String(editingRule.feePercent) : '',
        minSegments: editingRule.minSegments !== undefined && editingRule.minSegments !== null ? String(editingRule.minSegments) : '',
        minTotalSegments: editingRule.minTotalSegments !== undefined ? String(editingRule.minTotalSegments) : '',
        feeType,
      });
      setActiveStep(0);
      setInsertMode(false);
    } else if (editingRule && isCopying) {
      // Copying a rule — pre-fill everything but suggest next sequence number
      let feeType = '';
      if (editingRule.suppress === 'true' || editingRule.suppress === true) feeType = 'Suppress';
      else if (editingRule.oncePerBookingFeeAmount) feeType = 'OncePerBookingFeeAmount';
      else if (editingRule.feeAmount && editingRule.feePercent) feeType = 'FeeAmountAndPercent';
      else if (editingRule.feeAmount) feeType = 'FeeAmount';
      else if (editingRule.feePercent !== undefined && editingRule.feePercent !== null && editingRule.feePercent !== '') feeType = 'FeePercent';

      // Default: place it right after the copied rule
      const suggestedSeq = editingRule.sequenceNum + 1;

      setForm({
        ...INITIAL_STATE,
        ...editingRule,
        sequenceNum: String(suggestedSeq),
        feePercent: editingRule.feePercent !== undefined && editingRule.feePercent !== null ? String(editingRule.feePercent) : '',
        minSegments: editingRule.minSegments !== undefined && editingRule.minSegments !== null ? String(editingRule.minSegments) : '',
        minTotalSegments: editingRule.minTotalSegments !== undefined ? String(editingRule.minTotalSegments) : '',
        feeType,
      });
      setActiveStep(0);
      // Default to insert mode (shift others) since sequence is likely occupied
      const isOccupied = feeRules.some(r => r.sequenceNum === suggestedSeq);
      setInsertMode(isOccupied);
    } else {
      // New rule — suggest next available sequence number
      const maxSeq = feeRules.reduce((max, r) => Math.max(max, r.sequenceNum || 0), 0);
      setForm({ ...INITIAL_STATE, sequenceNum: String(maxSeq + 1) });
      setInsertMode(false);
    }
  }, [editingRule, isCopying, feeRules]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  const buildRule = () => {
    const rule = {
      carrier: form.carrier.trim(),
      sequenceNum: parseInt(form.sequenceNum),
    };

    if (form.origin) rule.origin = form.origin.trim();
    if (form.destination) rule.destination = form.destination.trim();

    if (form.feeType === 'Suppress') {
      rule.suppress = 'true';
    } else if (form.feeType === 'FeeAmount') {
      rule.feeAmount = form.feeAmount.trim();
    } else if (form.feeType === 'FeePercent') {
      rule.feePercent = parseFloat(form.feePercent);
    } else if (form.feeType === 'FeeAmountAndPercent') {
      rule.feeAmount = form.feeAmount.trim();
      rule.feePercent = parseFloat(form.feePercent);
    } else if (form.feeType === 'OncePerBookingFeeAmount') {
      rule.oncePerBookingFeeAmount = form.oncePerBookingFeeAmount.trim();
    }

    if (form.tripType) rule.tripType = form.tripType;
    if (form.cabinClass) rule.cabinClass = form.cabinClass;
    if (form.cabinClassMatchMode) rule.cabinClassMatchMode = form.cabinClassMatchMode;
    if (form.passengerType) rule.passengerType = form.passengerType;
    if (form.priceSource) rule.priceSource = form.priceSource;
    if (form.travelDateEffective) rule.travelDateEffective = form.travelDateEffective;
    if (form.travelDateDiscontinue) rule.travelDateDiscontinue = form.travelDateDiscontinue;
    if (form.ticketingDateEffective) rule.ticketingDateEffective = form.ticketingDateEffective;
    if (form.ticketingDateDiscontinue) rule.ticketingDateDiscontinue = form.ticketingDateDiscontinue;
    if (form.minPrice) rule.minPrice = form.minPrice.trim();
    if (form.maxPrice) rule.maxPrice = form.maxPrice.trim();
    if (form.minSegments) rule.minSegments = parseInt(form.minSegments);
    if (form.minTotalSegments) rule.minTotalSegments = parseInt(form.minTotalSegments);
    if (form.interline) rule.interline = form.interline;
    if (form.sliceInterline) rule.sliceInterline = form.sliceInterline;
    if (form.layoverMinutes) rule.layoverMinutes = parseInt(form.layoverMinutes);
    if (form.maxHoursToDeparture) rule.maxHoursToDeparture = parseInt(form.maxHoursToDeparture);

    return rule;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors([]);
    setFieldErrors({});

    const newFieldErrors = {};
    if (!form.carrier.trim()) newFieldErrors.carrier = 'Carrier is required';
    if (!form.sequenceNum) newFieldErrors.sequenceNum = 'Sequence number is required';
    if (!form.feeType) newFieldErrors.feeType = 'Fee type is required';
    if (form.feeType === 'FeeAmount' && !form.feeAmount.trim()) newFieldErrors.feeAmount = 'Fee amount is required';
    if (form.feeType === 'FeePercent' && form.feePercent === '') newFieldErrors.feePercent = 'Fee percent is required';
    if (form.feeType === 'FeeAmountAndPercent') {
      if (!form.feeAmount.trim()) newFieldErrors.feeAmount = 'Fee amount is required';
      if (form.feePercent === '') newFieldErrors.feePercent = 'Fee percent is required';
    }
    if (form.feeType === 'OncePerBookingFeeAmount' && !form.oncePerBookingFeeAmount.trim()) {
      newFieldErrors.oncePerBookingFeeAmount = 'Fee amount is required';
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setActiveStep(0);
      return;
    }

    const rule = buildRule();
    const validationErrors = validateFeeRule(rule, feeRules, locationGroups, carrierGroups);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isEditing) {
      updateFeeRule(editingRule.sequenceNum, rule);
    } else if (insertMode) {
      insertFeeRule(rule);
    } else {
      addFeeRule(rule);
    }

    if (onDone) onDone();
    else {
      const maxSeq = [...feeRules, rule].reduce((max, r) => Math.max(max, r.sequenceNum || 0), 0);
      setForm({ ...INITIAL_STATE, sequenceNum: String(maxSeq + 1) });
      setActiveStep(0);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {isEditing ? 'Edit Fee Rule' : isCopying ? 'Copy Fee Rule' : 'Add New Fee Rule'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? `Editing rule #${editingRule.sequenceNum}`
              : isCopying
                ? `Creating a copy of rule #${editingRule.sequenceNum} — modify and save as new`
                : 'Fill in the required fields to create a new rule'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditing && (
            <Chip label={`Editing #${editingRule.sequenceNum}`} color="primary" variant="outlined" />
          )}
          {isCopying && (
            <Chip label={`Copied from #${editingRule.sequenceNum}`} color="secondary" variant="outlined" />
          )}
        </Box>
      </Box>

      {/* Insert mode toggle (for copy and new rules) */}
      {!isEditing && (
        <Alert severity={insertMode ? 'warning' : 'info'} sx={{ mb: 3 }} icon={false}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2">
                {insertMode ? '⚠️ Insert Mode: ON' : 'ℹ️ Insert Mode: OFF'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {insertMode
                  ? 'Rules at or after the chosen sequence number will be shifted down automatically.'
                  : 'The sequence number must be unique. Turn on Insert Mode to auto-shift existing rules.'}
              </Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={insertMode} onChange={(e) => setInsertMode(e.target.checked)} color="warning" />}
              label="Insert & Shift"
              labelPlacement="start"
            />
          </Box>
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Validation Errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((err, i) => <li key={i}><Typography variant="body2">{err}</Typography></li>)}
          </ul>
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <form onSubmit={handleSubmit}>
        {/* Step 1: Basic Info */}
        {activeStep === 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Carrier & Fee Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Define the carrier this rule applies to and what fee/discount to apply.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Carrier"
                    value={form.carrier}
                    onChange={handleChange('carrier')}
                    error={!!fieldErrors.carrier}
                    helperText={fieldErrors.carrier || 'IATA code (LH), all (**), or group (U:1)'}
                    required
                    placeholder="e.g. LH, **, U:1"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth type="number"
                    label="Sequence Number"
                    value={form.sequenceNum}
                    onChange={handleChange('sequenceNum')}
                    error={!!fieldErrors.sequenceNum}
                    helperText={fieldErrors.sequenceNum || 'Processing order (first match wins)'}
                    required
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!fieldErrors.feeType}>
                    <InputLabel>Fee Type</InputLabel>
                    <Select value={form.feeType} label="Fee Type" onChange={handleChange('feeType')}>
                      <MenuItem value="FeePercent">Percentage Fee/Discount</MenuItem>
                      <MenuItem value="FeeAmount">Flat Fee/Discount (per passenger)</MenuItem>
                      <MenuItem value="FeeAmountAndPercent">Combined: Amount + Percent</MenuItem>
                      <MenuItem value="OncePerBookingFeeAmount">Once Per Booking (flat)</MenuItem>
                      <MenuItem value="Suppress">Suppress (block itinerary)</MenuItem>
                    </Select>
                    <FormHelperText>{fieldErrors.feeType || 'Select how the price adjustment is calculated'}</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Dynamic fee value fields */}
                {(form.feeType === 'FeePercent' || form.feeType === 'FeeAmountAndPercent') && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth type="number"
                      label="Fee Percent (%)"
                      value={form.feePercent}
                      onChange={handleChange('feePercent')}
                      error={!!fieldErrors.feePercent}
                      helperText={fieldErrors.feePercent || 'Positive = markup, negative = discount (e.g. -5.0)'}
                      required
                      inputProps={{ step: '0.1' }}
                      placeholder="e.g. 5.0 or -3.0"
                    />
                  </Grid>
                )}
                {(form.feeType === 'FeeAmount' || form.feeType === 'FeeAmountAndPercent') && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Fee Amount"
                      value={form.feeAmount}
                      onChange={handleChange('feeAmount')}
                      error={!!fieldErrors.feeAmount}
                      helperText={fieldErrors.feeAmount || 'Format: CurrencyAmount (e.g. USD5.00, GBP-2.50)'}
                      required
                      placeholder="e.g. USD5.00 or CHF-1.05"
                    />
                  </Grid>
                )}
                {form.feeType === 'OncePerBookingFeeAmount' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Booking Fee Amount"
                      value={form.oncePerBookingFeeAmount}
                      onChange={handleChange('oncePerBookingFeeAmount')}
                      error={!!fieldErrors.oncePerBookingFeeAmount}
                      helperText={fieldErrors.oncePerBookingFeeAmount || 'Must be positive (e.g. USD5.05)'}
                      required
                      placeholder="e.g. USD5.05"
                    />
                  </Grid>
                )}
                {form.feeType === 'Suppress' && (
                  <Grid item xs={12} md={6}>
                    <Alert severity="warning" variant="outlined">
                      This rule will <strong>block</strong> matching itineraries from being shown on Google Flights.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Route & Filters */}
        {activeStep === 1 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Route
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Leave blank for worldwide. Use N:XX for country, C:XXX for city, P:XXX for airport, U:N for location group.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Origin"
                    value={form.origin}
                    onChange={handleChange('origin')}
                    helperText="e.g. N:US, N:GB, P:JFK, C:NYC, U:1"
                    placeholder="Leave empty for worldwide"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Destination"
                    value={form.destination}
                    onChange={handleChange('destination')}
                    helperText="e.g. N:IN, N:DE, P:LHR, U:2"
                    placeholder="Leave empty for worldwide"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Selectors
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Further narrow down which itineraries this rule matches.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Trip Type</InputLabel>
                    <Select value={form.tripType} label="Trip Type" onChange={handleChange('tripType')}>
                      <MenuItem value="">Any (default)</MenuItem>
                      <MenuItem value="ONE_WAY">One Way</MenuItem>
                      <MenuItem value="ROUND_TRIP">Round Trip</MenuItem>
                      <MenuItem value="OPEN_JAW">Open Jaw (Multi-city 2)</MenuItem>
                      <MenuItem value="OTHER">Other (Multi-city 3+)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Cabin Class</InputLabel>
                    <Select value={form.cabinClass} label="Cabin Class" onChange={handleChange('cabinClass')}>
                      <MenuItem value="">Any (default)</MenuItem>
                      <MenuItem value="ECONOMY">Economy</MenuItem>
                      <MenuItem value="PREMIUM">Premium Economy</MenuItem>
                      <MenuItem value="BUSINESS">Business</MenuItem>
                      <MenuItem value="FIRST">First</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Passenger Type</InputLabel>
                    <Select value={form.passengerType} label="Passenger Type" onChange={handleChange('passengerType')}>
                      <MenuItem value="">Any (default)</MenuItem>
                      <MenuItem value="ANY">Any</MenuItem>
                      <MenuItem value="CHILD">Child / Infant in Seat</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Price Source</InputLabel>
                    <Select value={form.priceSource} label="Price Source" onChange={handleChange('priceSource')}>
                      <MenuItem value="">Any (default)</MenuItem>
                      <MenuItem value="GDS">GDS</MenuItem>
                      <MenuItem value="WEBSITE">Website / API</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Interline</InputLabel>
                    <Select value={form.interline} label="Interline" onChange={handleChange('interline')}>
                      <MenuItem value="">Any (default)</MenuItem>
                      <MenuItem value="true">Yes (multiple carriers)</MenuItem>
                      <MenuItem value="false">No (single carrier)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Slice Interline</InputLabel>
                    <Select value={form.sliceInterline} label="Slice Interline" onChange={handleChange('sliceInterline')}>
                      <MenuItem value="">Any (default)</MenuItem>
                      <MenuItem value="true">Yes (mixed on slice)</MenuItem>
                      <MenuItem value="false">No (single per slice)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth type="number"
                    label="Min Segments"
                    value={form.minSegments}
                    onChange={handleChange('minSegments')}
                    helperText="Per outbound or inbound"
                    inputProps={{ min: 0 }}
                    placeholder="0"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth type="number"
                    label="Min Total Segments"
                    value={form.minTotalSegments}
                    onChange={handleChange('minTotalSegments')}
                    helperText="Outbound + inbound combined"
                    inputProps={{ min: 0 }}
                    placeholder="0"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Dates & Price Range */}
        {activeStep === 2 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Travel Dates
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Restrict this rule to itineraries with their first flight within a date range.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth type="date"
                    label="Travel Date From"
                    value={form.travelDateEffective}
                    onChange={handleChange('travelDateEffective')}
                    InputLabelProps={{ shrink: true }}
                    helperText="First flight must be on or after this date"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth type="date"
                    label="Travel Date Until"
                    value={form.travelDateDiscontinue}
                    onChange={handleChange('travelDateDiscontinue')}
                    InputLabelProps={{ shrink: true }}
                    helperText="First flight must be before this date"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Ticketing Dates
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Control when this rule is active based on the ticketing/booking date.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth type="date"
                    label="Ticketing Date From"
                    value={form.ticketingDateEffective}
                    onChange={handleChange('ticketingDateEffective')}
                    InputLabelProps={{ shrink: true }}
                    helperText="Rule applies starting from this date"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth type="date"
                    label="Ticketing Date Until"
                    value={form.ticketingDateDiscontinue}
                    onChange={handleChange('ticketingDateDiscontinue')}
                    InputLabelProps={{ shrink: true }}
                    helperText="Rule expires on this date"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Price Range
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Match only when the per-passenger price falls within this range.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Min Price"
                    value={form.minPrice}
                    onChange={handleChange('minPrice')}
                    helperText="e.g. USD100, GBP0, AED900"
                    placeholder="CurrencyValue (e.g. USD100)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Price"
                    value={form.maxPrice}
                    onChange={handleChange('maxPrice')}
                    helperText="Same currency as Min Price"
                    placeholder="CurrencyValue (e.g. USD500)"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Navigation & Submit */}
        <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2 }}>
          <Button
            variant="outlined"
            disabled={activeStep === 0}
            onClick={() => setActiveStep(prev => prev - 1)}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setActiveStep(prev => prev + 1)}
              >
                Next
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setForm(INITIAL_STATE);
                    setErrors([]);
                    setFieldErrors({});
                    setActiveStep(0);
                  }}
                >
                  Reset
                </Button>
                <Button type="submit" variant="contained" color="success" size="large">
                  {isEditing ? '✓ Update Rule' : '✓ Add Rule'}
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </form>
    </Box>
  );
}
